import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import {
  IManifestContextBuilder,
  ManifestContextAPI,
} from '@wix/editor-platform-manifest-services';

import { loadPanelService } from '../automaticPanel/panelsService';
import { AuthenticationStrategy, Host, WixClient } from '@wix/sdk';
import { element, elements } from '@wix/editor';
import { STYLE } from '@wix/component-protocol/schema';
import { CompRef, StyleVariableValueValue } from '@wix/document-services-types';
import { EditorPointer } from '@wix/editorx-core-api';
import { StyleItem } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME } from '../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { GroupingStrategy } from './common/grouping/types';

export type { StylesDerivedNativeActionNames };
export type ShadowStyleValue = Extract<
  StyleVariableValueValue,
  { type: 'BoxShadow' }
>;
export type SpacingStyleValue = Extract<
  StyleVariableValueValue,
  { type: 'Length' }
>;

export type CoordinatesStyleValue = Extract<
  StyleVariableValueValue,
  { type: 'Coordinates' }
>;

export type GapShorthandValue = Extract<
  StyleVariableValueValue,
  { type: 'Gap' }
>;

export type CssPropertyType = keyof typeof STYLE.CSS_PROPERTY_TYPE;
export type StyleDefinition = {
  cssPropertyType: CssPropertyType;
  defaultValue?: string;
};
export type StyleDefinitions = {
  [key: string]: StyleDefinition;
};

export type GlobalDesignActionPanel = React.ComponentType<{
  host: Host;
}>;

export type GlobalDesignActionLobbyItem = React.ComponentType<{
  group: GlobalDesignStyleGroup;
  onSelect: () => void;
}>;

export type ElementsSDKClient = WixClient<
  any,
  AuthenticationStrategy<any>,
  {
    element: typeof element;
    elements: typeof elements;
  }
>;

export interface GlobalDesignStyleItem<
  TActionName extends StylesDerivedNativeActionNames,
> extends StyleItem {
  cssPropertyType: (typeof ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME)[TActionName][number];
}

export type DerivedActionStyle = Record<
  string,
  GlobalDesignStyleItem<StylesDerivedNativeActionNames>
>;

type LinkedStyleItem<TActionName extends StylesDerivedNativeActionNames> = {
  editorPointer: EditorPointer<CompRef>;
  styleItemId: string;
  styleItem: GlobalDesignStyleItem<TActionName>;
};
export interface StylesGroup<
  TActionName extends StylesDerivedNativeActionNames,
> {
  displayName: string;
  editorPointer: EditorPointer<CompRef>;
  items: {
    styleItemId: string;
    styleItem: GlobalDesignStyleItem<TActionName>;
    linkedStyleItems?: LinkedStyleItem<TActionName>[];
  }[];
  context: ManifestContextAPI;
}

export enum ManifestProcessingStrategy {
  Root = 'root',
  Recursive = 'recursive',
}

export type StyleFilter = (
  styles: DerivedActionStyle,
  context: ManifestContextAPI,
  manifestProcessingStrategy: ManifestProcessingStrategy,
) => Promise<DerivedActionStyle>;

export type GlobalDesignStyleGroup = {
  displayName: string;
  host: Host;
};
export type GlobalDesignAction<
  TActionName extends StylesDerivedNativeActionNames,
> = {
  panel: GlobalDesignActionPanel;
  lobbyItem: GlobalDesignActionLobbyItem;
  getManifestProcessingStrategy?: (
    context: ManifestContextAPI,
  ) => Promise<ManifestProcessingStrategy>;
  valueFilter?: StyleFilter;
  grouping: {
    getGroupingStrategy: (
      editorPointer: EditorPointer<CompRef>,
      getHost: GlobalDesignPanelProps<TActionName>['getHost'],
    ) => GroupingStrategy;
    applyValueMergingStrategy?: (
      stylesGroups: StylesGroup<TActionName>[],
    ) => Promise<StylesGroup<TActionName>[]>; // TODO type better, this step is required only for default grouping strategy
  };
};

export type GlobalDesignPanelProps<
  TActionName extends StylesDerivedNativeActionNames,
> = {
  editorPointer: EditorPointer<CompRef>;
  actionName: TActionName;
  getHost: (props?: {
    contextBuilder?: IManifestContextBuilder;
  }) => Promise<Host>;
};

export type ControlService = {
  getControl: Awaited<ReturnType<typeof loadPanelService>>['getControl'];
  styles: { [key: string]: { value: StyleVariableValueValue } };
  styleDefinitions: StyleDefinitions;
  setStyles: (updatedStyles: {
    [key: string]: StyleVariableValueValue;
  }) => void;
};

export type CombinedControlTypes = 'ShadowOpacity' | 'ShadowBlur';

type SupoportedCombinedValue = ShadowStyleValue;

export type CombinedValueControlProps = {
  getControl: ControlService['getControl'];
  combinedValue: SupoportedCombinedValue;
  onCombinedValueChange: (updatedPart: { [key: string]: any }) => void;
  mixMode: boolean;
};
